
import React, { useCallback, useEffect, useMemo, useState } from "react";

import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./Admintopbar";

import {
  getInstituteAccounts,
  createInstituteAccount,
  updateInstituteAccountStatus,
  resetInstituteAccountPassword,
} from "../api/adminclient";

import { apiErrorMessage } from "../api/auth";
import AdminInstitutePayments from "./AdminInstitutePayments";

const EMPTY_FORM = {
  name: "",
  username: "",
  email: "",
};

const getErrorMessage = (error, fallback) =>
  apiErrorMessage(error, fallback);

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export default function AdminInstitutes() {
  const [activeSection, setActiveSection] = useState("institutes");
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [busyId, setBusyId] = useState(null);

  const loadInstitutes = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const response = await getInstituteAccounts();

      const data = response?.data;

      setInstitutes(
        Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data?.institutes)
              ? data.institutes
              : []
      );
    } catch (error) {
      setLoadError(
        getErrorMessage(
          error,
          "Could not load institute accounts. Check the Django server and your admin permissions."
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInstitutes();
  }, [loadInstitutes]);

  const filteredInstitutes = useMemo(() => {
    const query = search.trim().toLowerCase();

    return institutes.filter((institute) => {
      const matchesSearch =
        !query ||
        String(institute.name || "").toLowerCase().includes(query) ||
        String(institute.username || "").toLowerCase().includes(query) ||
        String(institute.email || "").toLowerCase().includes(query);

      const active = institute.is_active === true;

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" && active) ||
        (statusFilter === "Inactive" && !active);

      return matchesSearch && matchesStatus;
    });
  }, [institutes, search, statusFilter]);

  const activeCount = institutes.filter(
    (institute) => institute.is_active === true
  ).length;

  const updateFormField = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const openCreateForm = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setActionError("");
    setSuccessMessage("");
    setShowForm(true);
  };

  const closeCreateForm = () => {
    if (saving) return;

    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    setFormError("");
    setActionError("");
    setSuccessMessage("");

    if (
      !form.name.trim() ||
      !form.username.trim() ||
      !form.email.trim()
    ) {
      setFormError("Please complete all three fields.");
      return;
    }

    setSaving(true);

    try {
      const response = await createInstituteAccount(form);

      setSuccessMessage(
        response?.data?.message ||
          "Institute account created. Check the response and the institute's email for credential-delivery status."
      );

      setForm(EMPTY_FORM);
      setShowForm(false);

      await loadInstitutes();
    } catch (error) {
      setFormError(
        getErrorMessage(
          error,
          "Could not create the institute account."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = async (institute) => {
    const currentlyActive = institute.is_active === true;
    const nextStatus = !currentlyActive;

    const confirmed = window.confirm(
      `${nextStatus ? "Activate" : "Deactivate"} the account for "${institute.name}"?`
    );

    if (!confirmed) return;

    setBusyId(institute.id);
    setActionError("");
    setSuccessMessage("");

    try {
      await updateInstituteAccountStatus(institute.id, nextStatus);

      setInstitutes((current) =>
        current.map((item) =>
          item.id === institute.id
            ? { ...item, is_active: nextStatus }
            : item
        )
      );

      setSuccessMessage(
        `${institute.name} account ${nextStatus ? "activated" : "deactivated"}.`
      );
    } catch (error) {
      setActionError(
        getErrorMessage(
          error,
          "Could not update the institute account status."
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  const handlePasswordReset = async (institute) => {
    const confirmed = window.confirm(
      `Reset the password for "${institute.name}"? A new temporary password will be generated and emailed.`
    );

    if (!confirmed) return;

    setBusyId(institute.id);
    setActionError("");
    setSuccessMessage("");

    try {
      const response = await resetInstituteAccountPassword(institute.id);

      setInstitutes((current) =>
        current.map((item) =>
          item.id === institute.id
            ? { ...item, must_change_password: true }
            : item
        )
      );

      setSuccessMessage(
        response?.data?.message ||
          `Password reset requested for ${institute.name}. Check the backend response for confirmation.`
      );
    } catch (error) {
      setActionError(
        getErrorMessage(
          error,
          "Could not reset the institute password."
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-layout d-flex">
      <AdminSidebar />

      <main className="admin-main flex-grow-1">
        <AdminTopbar />

        <div className="container-fluid p-3 p-md-4">
          <div className="d-flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Institute administration sections">
            <button
              type="button"
              role="tab"
              aria-selected={activeSection === "institutes"}
              className={`btn ${activeSection === "institutes" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveSection("institutes")}
            >
              <i className="bi bi-buildings me-2" />
              Manage Institutes
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeSection === "payments"}
              className={`btn ${activeSection === "payments" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveSection("payments")}
            >
              <i className="bi bi-credit-card me-2" />
              Institute Payment Approvals
            </button>
          </div>

          {activeSection === "payments" ? (
            <AdminInstitutePayments />
          ) : (
          <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1">
                Institute Accounts
              </h2>

              <p className="text-muted mb-0">
                Create and manage institute administrator access.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={openCreateForm}
            >
              <i className="bi bi-plus-lg me-2" />
              Create Institute
            </button>
          </div>

          {successMessage && (
            <div
              className="alert alert-success alert-dismissible"
              role="status"
            >
              {successMessage}

              <button
                type="button"
                className="btn-close"
                aria-label="Dismiss"
                onClick={() => setSuccessMessage("")}
              />
            </div>
          )}

          {actionError && (
            <div className="alert alert-danger" role="alert">
              {actionError}
            </div>
          )}

          {showForm && (
            <section className="admin-card p-3 p-md-4 mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">
                  Create Institute Account
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeCreateForm}
                  disabled={saving}
                />
              </div>

              <p className="text-muted small">
                Enter the institute details. The backend generates
                the temporary password and sends the credentials
                by email.
              </p>

              <form onSubmit={handleCreate}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label
                      htmlFor="institute-name"
                      className="form-label"
                    >
                      Institute Name
                    </label>

                    <input
                      id="institute-name"
                      name="name"
                      className="form-control"
                      value={form.name}
                      onChange={updateFormField}
                      required
                      maxLength={150}
                      autoComplete="organization"
                    />
                  </div>

                  <div className="col-md-4">
                    <label
                      htmlFor="institute-username"
                      className="form-label"
                    >
                      Username
                    </label>

                    <input
                      id="institute-username"
                      name="username"
                      className="form-control"
                      value={form.username}
                      onChange={updateFormField}
                      required
                      autoComplete="off"
                    />
                  </div>

                  <div className="col-md-4">
                    <label
                      htmlFor="institute-email"
                      className="form-label"
                    >
                      Email Address
                    </label>

                    <input
                      id="institute-email"
                      name="email"
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={updateFormField}
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>

                {formError && (
                  <div className="alert alert-danger mt-3 mb-0">
                    {formError}
                  </div>
                )}

                <div className="d-flex gap-2 mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? "Creating..." : "Create Account"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeCreateForm}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </section>
          )}

          <div className="row g-3 mb-4">
            <div className="col-sm-6 col-xl-4">
              <div className="admin-card p-3 p-md-4 h-100">
                <div className="text-muted small">
                  Total Institutes
                </div>

                <div className="fs-3 fw-bold">
                  {institutes.length}
                </div>
              </div>
            </div>

            <div className="col-sm-6 col-xl-4">
              <div className="admin-card p-3 p-md-4 h-100">
                <div className="text-muted small">
                  Active Accounts
                </div>

                <div className="fs-3 fw-bold text-success">
                  {activeCount}
                </div>
              </div>
            </div>

            <div className="col-sm-6 col-xl-4">
              <div className="admin-card p-3 p-md-4 h-100">
                <div className="text-muted small">
                  Inactive Accounts
                </div>

                <div className="fs-3 fw-bold text-secondary">
                  {institutes.length - activeCount}
                </div>
              </div>
            </div>
          </div>

          <section className="admin-card p-3 p-md-4">
            <div className="row g-3 mb-4">
              <div className="col-md-8">
                <label
                  htmlFor="institute-search"
                  className="form-label"
                >
                  Search Institutes
                </label>

                <input
                  id="institute-search"
                  type="search"
                  className="form-control"
                  placeholder="Search name, username or email..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label
                  htmlFor="institute-status"
                  className="form-label"
                >
                  Account Status
                </label>

                <select
                  id="institute-status"
                  className="form-select"
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value)
                  }
                >
                  <option value="All">All Accounts</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            {loading && (
              <p className="text-muted mb-0">
                Loading institute accounts...
              </p>
            )}

            {!loading && loadError && (
              <div className="alert alert-danger">
                <p className="mb-2">{loadError}</p>

                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={loadInstitutes}
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !loadError && filteredInstitutes.length === 0 && (
              <p className="text-muted mb-0">
                {institutes.length === 0
                  ? "No institute accounts have been created yet."
                  : "No institutes match your search or filter."}
              </p>
            )}

            {!loading && !loadError && filteredInstitutes.length > 0 && (
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Institute</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Password</th>
                      <th>Created</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredInstitutes.map((institute, index) => {
                      const isActive = institute.is_active === true;
                      const busy = busyId === institute.id;

                      return (
                        <tr key={institute.id}>
                          <td>{index + 1}</td>

                          <td className="fw-semibold">
                            {institute.name || "—"}
                          </td>

                          <td>{institute.username || "—"}</td>

                          <td>{institute.email || "—"}</td>

                          <td>
                            <span
                              className={`badge ${
                                isActive
                                  ? "bg-success"
                                  : "bg-secondary"
                              }`}
                            >
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </td>

                          <td>
                            {institute.must_change_password ? (
                              <span className="badge bg-warning text-dark">
                                Change required
                              </span>
                            ) : (
                              <span className="badge bg-light text-dark border">
                                Set
                              </span>
                            )}
                          </td>

                          <td>
                            {formatDate(institute.created_at)}
                          </td>

                          <td>
                            <div className="d-flex justify-content-end flex-wrap gap-2">
                              <button
                                type="button"
                                className={`btn btn-sm ${
                                  isActive
                                    ? "btn-outline-danger"
                                    : "btn-outline-success"
                                }`}
                                disabled={busy}
                                onClick={() =>
                                  handleStatusToggle(institute)
                                }
                              >
                                {busy
                                  ? "Please wait..."
                                  : isActive
                                    ? "Deactivate"
                                    : "Activate"}
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                disabled={busy || !isActive}
                                title={
                                  !isActive
                                    ? "Activate this account before resetting its password."
                                    : "Generate a new temporary password"
                                }
                                onClick={() =>
                                  handlePasswordReset(institute)
                                }
                              >
                                Reset Password
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          </>
          )}
        </div>
      </main>
    </div>
  );
}
